import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Check, Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { PRIVACY_POLICY, PRIVACY_POLICY_VERSION } from '@/constants/privacyPolicy';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Global modal that blocks the entire app until the current user accepts
 * the latest privacy policy version. Triggered after first login + password
 * change, or whenever the policy version is bumped.
 *
 * Admins are exempt — they manage policy on behalf of the organisation.
 */
export default function PrivacyPolicyGuard({ children }) {
  const { user } = useAuth();
  // Policy language follows the employee's notification preference, NOT the
  // UI language. Admins (who are exempt from the guard anyway) fall back to
  // Arabic by default.
  const isAr = (user?.notification_language || 'ar') === 'ar';
  const T = PRIVACY_POLICY[isAr ? 'ar' : 'en'];

  const [status, setStatus] = useState({ checked: false, required: false });
  const [scrolledEnough, setScrolledEnough] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const scrollRef = useRef(null);

  /* ----- Fetch privacy status on user change ----- */
  useEffect(() => {
    if (!user) {
      setStatus({ checked: true, required: false });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/privacy-status`, { withCredentials: true });
        if (!cancelled) {
          // Defer requiring acceptance until any "must change password" flow
          // is finished — those screens use the same auth context.
          const required = data.required && !data.must_change_password;
          setStatus({ checked: true, required });
        }
      } catch {
        if (!cancelled) setStatus({ checked: true, required: false });
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  /* ----- Scroll detection inside the policy body ----- */
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const reachedBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
    if (reachedBottom && !scrolledEnough) setScrolledEnough(true);
  }, [scrolledEnough]);

  useEffect(() => {
    if (!status.required) return;
    const el = scrollRef.current;
    if (!el) return;
    // If content already fits, auto-unlock.
    if (el.scrollHeight <= el.clientHeight + 8) setScrolledEnough(true);
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [status.required, handleScroll]);

  const submit = async () => {
    if (!accepted || !scrolledEnough || saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/auth/accept-privacy`,
        { version: PRIVACY_POLICY_VERSION },
        { withCredentials: true },
      );
      toast.success(isAr ? 'تم تسجيل موافقتك بنجاح' : 'Acceptance recorded');
      setStatus(s => ({ ...s, required: false }));
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'تعذّر حفظ الموافقة' : 'Could not save acceptance'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {children}
      <AnimatePresence>
        {status.checked && status.required && (
          <motion.div
            key="privacy-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-8"
            data-testid="privacy-policy-overlay"
            dir={isAr ? 'rtl' : 'ltr'}
          >
            {/* Backdrop */}
            <div
              aria-hidden
              className="absolute inset-0 bg-[#1F1A33]/55 backdrop-blur-md"
            />

            {/* Modal card */}
            <motion.div
              initial={{ scale: 0.96, y: 12 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 12 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10 w-full max-w-3xl max-h-[88vh] flex flex-col rounded-2xl border border-white dark:border-[#322b58] bg-white shadow-[0_24px_80px_rgba(101,93,198,0.25)] overflow-hidden"
            >
              {/* Accent top strip */}
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-[#655dc6] to-transparent" />

              {/* Header */}
              <div className="px-6 md:px-10 py-6 border-b border-[#E8E6F5] flex items-start gap-4 flex-shrink-0">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md"
                  style={{ background: '#655dc6' }}
                >
                  <Shield size={22} className="text-white" strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl md:text-2xl font-bold text-[#1F2937]">{T.title}</h2>
                  <p className="text-xs text-[#6B7280] mt-1">{T.subtitle}</p>
                </div>
              </div>

              {/* Scrollable body */}
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-6 md:px-10 py-6 space-y-5 privacy-scroll bg-[#FBFAFE]"
                data-testid="privacy-policy-body"
              >
                <p className="text-sm md:text-base leading-relaxed text-[#374151]">{T.intro}</p>

                {T.clauses.map((c, i) => (
                  <section key={i} className="space-y-2">
                    <h3 className="text-base md:text-lg font-bold text-[#1F2937] flex items-center gap-2.5">
                      <span className="text-xs text-white bg-[#655dc6] tabular-nums w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0">{(i + 1).toString().padStart(2, '0')}</span>
                      <span>{c.title}</span>
                    </h3>
                    {c.body && <p className="text-sm leading-relaxed text-[#4B5563] ms-9">{c.body}</p>}
                    {c.items && (
                      <ul className="space-y-1.5 ms-9">
                        {c.items.map((item, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-[#4B5563] leading-relaxed">
                            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#655dc6] flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                ))}

                <div className="h-2" />
              </div>

              {/* Scroll hint */}
              {!scrolledEnough && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute bottom-[150px] inset-x-0 flex justify-center pointer-events-none"
                >
                  <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#655dc6] text-white text-xs font-medium shadow-lg">
                    <ChevronDown size={14} className="animate-bounce" />
                    {T.scroll_hint}
                  </div>
                </motion.div>
              )}

              {/* Footer */}
              <div className="px-6 md:px-10 py-5 border-t border-[#E8E6F5] bg-white flex-shrink-0">
                <label
                  className={`flex items-start gap-3 cursor-pointer select-none ${!scrolledEnough ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid="privacy-accept-label"
                >
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    disabled={!scrolledEnough}
                    className="mt-1 w-4 h-4 rounded border-[#C4BFF0] accent-[#655dc6] cursor-pointer disabled:cursor-not-allowed"
                    data-testid="privacy-accept-checkbox"
                  />
                  <span className="text-sm text-[#1F2937] leading-relaxed font-medium">{T.accept_label}</span>
                </label>

                <button
                  onClick={submit}
                  disabled={!accepted || !scrolledEnough || saving}
                  className="mt-4 w-full h-12 rounded-lg font-semibold text-white transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
                  style={{
                    background: '#655dc6',
                    boxShadow: '0 10px 28px rgba(101, 93, 198, 0.28)',
                  }}
                  onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = '#544cb0'; }}
                  onMouseLeave={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = '#655dc6'; }}
                  data-testid="privacy-accept-button"
                >
                  {saving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                  {saving ? T.saving : T.accept_btn}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
